import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, tap, finalize } from 'rxjs/operators';

import { UserInfoService, LoginInfoInStorage } from './user-info.service';
import { ApiRequestService } from './api-request.service';
import { AgentService } from './agent.service';
import { ChangePassService } from './change-pass.service';
import { AppMetadataHandler } from 'app/BaseFramework/configs';

/**
 * Interface for login request parameters
 */
export interface LoginRequestParam {
    userId: string;
    password: string;
}

/**
 * Service responsible for handling authentication-related operations
 * including login, logout, and password management
 */
@Injectable()
export class LoginService {
    /**
     * Default landing page after successful login
     */
    public landingPage: string = "/app/dash";

    /**
     * @param router - Angular router for navigation
     * @param userInfoService - Service to manage user information storage
     * @param apiRequest - Service for API communication
     * @param agentService - Service for agent-related functionality
     * @param changePassService - Service for password encryption and management
     * @param appMetaDataHandler - Service for application metadata
     */
    constructor(
        private router: Router,
        private userInfoService: UserInfoService,
        private apiRequest: ApiRequestService,
        private agentService: AgentService,
        private changePassService: ChangePassService,
        private appMetaDataHandler: AppMetadataHandler
    ) { }

    /**
     * Authenticates a user and retrieves an authentication token
     * 
     * @param userId - User identifier
     * @param password - User password (unencrypted)
     * @returns Observable that emits login information
     */
    getToken(userId: string, password: string): Observable<LoginInfoInStorage> {
        // Initialize login response object with default failure state
        const loginInfoReturn: LoginInfoInStorage = this.getEmptyLoginInfo(userId);
        const loginDataSubject = new Subject<LoginInfoInStorage>();
        
        // Prepare request body with encrypted password
        const bodyData: LoginRequestParam = {
            userId,
            password: this.changePassService.encrypted(password)
        };

        // Authenticate user
        this.apiRequest.authenticate('', bodyData)
            .pipe(
                catchError(error => {
                    loginDataSubject.next(loginInfoReturn);
                    loginDataSubject.complete();
                    return throwError(error);
                })
            )
            .subscribe(jsonResp => {
                if (jsonResp?.responseCode === 'SUCCESS') {
                    // Check if user needs to reset password
                    const hasExceptions = jsonResp.user.exceptionDOList?.length > 0 && 
                                         jsonResp.user.exceptionDOList[0] != null;
                    
                    if (hasExceptions) {
                        jsonResp.user.oneTimePasswordFl = true;
                    }

                    // Update login info with successful response data
                    loginInfoReturn.success = true;
                    loginInfoReturn.message = jsonResp.Message;
                    loginInfoReturn.landingPage = this.landingPage;
                    
                    // Set user information
                    Object.assign(loginInfoReturn.user, {
                        userId: bodyData.userId,
                        firstName: jsonResp.user.firstName,
                        email: jsonResp.user.emailId,
                        displayName: jsonResp.user.firstName,
                        oneTimePasswordFl: jsonResp.user.oneTimePasswordFl,
                        statusCd: jsonResp.user.statusCd,
                        typeCd: jsonResp.user.typeCd,
                        baseCompanyCd: jsonResp.user.baseCompanyCd,
                        userRoleDOList: jsonResp.user.userRoleDOList,
                        userObjectResp: jsonResp.user, // Security note: Consider filtering sensitive data
                        token: this.apiRequest.token
                    });

                    // Store user info in session storage
                    this.userInfoService.storeUserInfo(JSON.stringify(jsonResp.user));
                    
                    // Fetch company details if available
                    if (loginInfoReturn.user.baseCompanyCd) {
                        this.fetchCompanyByNum(loginInfoReturn.user.baseCompanyCd);
                    }
                }
                
                loginDataSubject.next(loginInfoReturn);
                loginDataSubject.complete();
            });

        return loginDataSubject.asObservable();
    }

    /**
     * Logs out the current user and optionally navigates to logout page
     * 
     * @param navigateToLogout - Whether to navigate to logout page after clearing user data
     */
    logout(navigateToLogout = true): void {
        this.userInfoService.removeUserInfo();
        
        if (navigateToLogout) {
            this.router.navigate(["logout"]);
        }
    }

    /**
     * Creates an empty login info object with failure state
     * 
     * @param userId - User identifier to include in the empty login info
     * @returns Empty login info object
     */
    private getEmptyLoginInfo(userId: string): LoginInfoInStorage {
        return {
            success: false,
            message: "Unsuccessful login",
            landingPage: "/login",
            user: {
                userId: userId,
                firstName: null,
                email: null,
                displayName: null,
                token: null,
                oneTimePasswordFl: false,
                statusCd: null,
                typeCd: null,
                userRoleDOList: [],
                userObjectResp: null,
                baseCompanyCd: null
            }
        };
    }

    /**
     * Logs out an agent by making a server call and clearing all local storage
     */
    logoutAgent(): Observable<any> {
        const logoutSubject = new Subject<any>();
        
        this.apiRequest.getCallWithAPIMapper('logout')
            .pipe(
                finalize(() => {
                    // Clear all storage regardless of API response
                    localStorage.clear();
                    sessionStorage.clear();
                    this.userInfoService.removeUserInfo();
                    this.router.navigate(['/login']);
                })
            )
            .subscribe(
                response => {
                    logoutSubject.next(response);
                    logoutSubject.complete();
                },
                error => {
                    logoutSubject.error(error);
                }
            );
            
        return logoutSubject.asObservable();
    }
    // is pipe map and tap more efficient than using a subject and returning it

    /**
     * Submits a password reset request
     * 
     * @param userId - User identifier
     * @param currentPassword - Current password (unencrypted)
     * @param newPassword - New password (unencrypted)
     * @returns Observable with the reset response
     */
    submitPasswordResetRequest(userId: string, currentPassword: string, newPassword: string): Observable<any> {
        const resetPayload = {
            size: 20,
            searchCriteriaList: [
                {
                    paramName: "currenPass",
                    paramValue: this.changePassService.encrypted(currentPassword)
                },
                {
                    paramName: "confirmPass",
                    paramValue: this.changePassService.encrypted(newPassword)
                },
                {
                    paramName: "userId",
                    paramValue: userId
                }
            ]
        };

        const resetResponse$ = new Subject<any>();

        this.apiRequest.postCallWithAPIMapper('changePassword', resetPayload)
            .pipe(
                catchError(error => {
                    resetResponse$.error(error);
                    return throwError(error);
                })
            )
            .subscribe(response => {
                resetResponse$.next(response);
                resetResponse$.complete();
            });
        
        return resetResponse$.asObservable();
    }

    /**
     * Fetches company details by company number and updates application metadata
     * 
     * @param companyNum - Company number to fetch details for
     */
    fetchCompanyByNum(companyNum: string): void {
        const url = `user-service/company/fetch-by-companyNum/${companyNum}`;
        
        this.apiRequest.get(url)
            .pipe(
                tap(companyResp => {
                    if (companyResp?.companyMasterDO) {
                        // Update application metadata with company settings
                        this.appMetaDataHandler.companyCurrencyCd = companyResp.companyMasterDO.currencyCd;
                        this.appMetaDataHandler.GLOBAL_DATE_FORMAT_ANGULAR = 
                            companyResp.companyMasterDO.dateFormatCd || "dd MMM yyyy";
                        this.appMetaDataHandler.GLOBAL_DATE_FORMAT_MOMENT = 
                            companyResp.companyMasterDO.dateFormatCd || "dd MMM yyyy";
                    }
                }),
                catchError(error => {
                    console.error('Error fetching company details:', error);
                    return throwError(error);
                })
            )
            .subscribe();
    }
}